// Llamadas al backend para recuperar y cambiar la clave.
//
// No hace falta tocar get-client.js: su interceptor ya borra el header
// Authorization cuando no hay token, así que los endpoints públicos funcionan
// tal cual.
//
// OJO con los timeouts: un POST que se pasa de tiempo NO se reintenta (ver
// get-client.js). Eso es a propósito, porque el request pudo haber llegado y
// ejecutado igual. Por eso el mensaje de timeout de acá es distinto del de red
// y dice explícitamente que la acción pudo haber pasado.
import getClient, { esRespuestaAjena } from "./get-client";
import { AUTH_ERROR_MESSAGES, getCsrfToken } from "./authentication-helpers";
import { establecerSesion } from "./session-token";

const client = getClient();
const TIMEOUT_MS = 30000;

export const RESET_ERROR_MESSAGES = {
  ...AUTH_ERROR_MESSAGES,
  timeoutReset:
    "El servidor no respondió a tiempo. Puede que el correo se haya enviado igual: revisa tu bandeja antes de volver a pedirlo.",
  timeoutConfirm:
    "El servidor no respondió a tiempo. Prueba entrar con tu clave nueva antes de repetir esto.",
  deadLink:
    "Este enlace ya no sirve. Los enlaces vencen a los 30 minutos y sirven una sola vez.",
  wrongCurrent: "Tu clave actual no coincide.",
};

/**
 * Traduce un error de axios a { kind, message }.
 *
 * Mismo criterio que describeAuthError: un 403 no manda a revisar la red, y un
 * 410 acá significa "enlace muerto", no "error del servidor".
 */
export const describeResetError = (error, { context = "" } = {}) => {
  if (!error) return { kind: "server", message: RESET_ERROR_MESSAGES.server };

  const status = error?.response?.status;
  const data = error?.response?.data;

  if (error.code === "ECONNABORTED") {
    const message =
      context === "reset"
        ? RESET_ERROR_MESSAGES.timeoutReset
        : context === "confirm"
          ? RESET_ERROR_MESSAGES.timeoutConfirm
          : RESET_ERROR_MESSAGES.timeout;
    return { kind: "timeout", message };
  }
  if (!error.response) {
    return { kind: "network", message: RESET_ERROR_MESSAGES.network };
  }
  // Mismo criterio que el login (INC-2026-31): un 403 que no salió de nuestro
  // backend es el filtro de la institución, no un problema de la cuenta. Sin
  // esto, un médico bloqueado que viene a recuperar su clave —o sea el que ya
  // está convencido de que el problema es su contraseña— leía el mensaje
  // genérico y seguía dándole vueltas a la clave.
  if (esRespuestaAjena(error)) {
    return { kind: "network", message: RESET_ERROR_MESSAGES.network };
  }
  if (status === 410) {
    return { kind: "dead", message: RESET_ERROR_MESSAGES.deadLink };
  }
  if (status === 429) {
    return { kind: "throttled", message: RESET_ERROR_MESSAGES.throttled };
  }
  if (status === 503) {
    return {
      kind: "server",
      message: "La recuperación de clave está fuera de servicio por un momento.",
    };
  }
  if (status === 400) {
    // El backend manda los mensajes de los validadores de clave; se muestran
    // tal cual porque explican QUÉ le falta a la clave.
    const detalle =
      data?.new_password?.[0] ||
      data?.current_password?.[0] ||
      data?.detail ||
      data?.email?.[0];
    return { kind: "invalid", message: detalle || RESET_ERROR_MESSAGES.credentials };
  }
  if (status >= 500) {
    return { kind: "server", message: RESET_ERROR_MESSAGES.server };
  }
  return { kind: "server", message: RESET_ERROR_MESSAGES.server };
};

const post = async (url, payload) => {
  const csrf = await getCsrfToken();
  return client.post(url, payload, {
    timeout: TIMEOUT_MS,
    headers: csrf ? { "X-CSRFToken": csrf } : {},
    withCredentials: true,
  });
};

/** Pide el enlace. La respuesta es la MISMA exista o no la cuenta. */
export const requestPasswordReset = (email) =>
  post("/api/user/password/reset/", { email: (email || "").trim() });

/** Chequea el enlace ANTES de que el médico escriba la clave dos veces. */
export const verifyResetToken = (token) =>
  post("/api/user/password/reset/verify/", { token });

/** Setea la clave nueva. No devuelve sesión: hay que entrar con la clave nueva. */
export const confirmPasswordReset = (token, newPassword) =>
  post("/api/user/password/reset/confirm/", { token, new_password: newPassword });

/** Cambia la clave estando adentro. Devuelve un token de sesión NUEVO. */
export const changePassword = (currentPassword, newPassword) =>
  post("/api/user/password/change/", {
    current_password: currentPassword,
    new_password: newPassword,
  });

/** Cierra la sesión en los otros aparatos. Devuelve un token NUEVO para este. */
export const revokeOtherSessions = () =>
  post("/api/user/sessions/revoke-others/", {});

/**
 * Guarda el token nuevo que devuelve el backend tras rotar la sesión.
 *
 * Esto NO es opcional. No hay manejo global de 401 en el cliente (get-client.js
 * lo dice: "401/403 quedan para una entrega posterior"), así que si el backend
 * rota el token y el front se queda con el viejo, el médico no ve un redirect
 * al login: ve la app cargada y todo fallando en silencio.
 *
 * Devuelve true si quedó una sesión válida, false si hay que mandar al login.
 */
export const adoptRotatedToken = (data) =>
  establecerSesion(data?.token, { mismoUsuario: true });

// Reglas de clave. Están duplicadas a propósito con el backend (que las corre
// de verdad con los validadores de Django): acá son para dar feedback en vivo,
// no para autorizar nada.
export const passwordRules = (password, email = "") => {
  const value = password || "";
  const local = (email || "").split("@")[0].toLowerCase();
  return [
    { id: "largo", label: "Al menos 10 caracteres", ok: value.length >= 10 },
    { id: "numeros", label: "No es solo números", ok: !/^\d*$/.test(value) || value === "" },
    {
      id: "correo",
      label: "No contiene tu correo",
      ok: !local || local.length < 3 || !value.toLowerCase().includes(local),
    },
  ];
};

export const passwordStrength = (password, email = "") => {
  const cumplidas = passwordRules(password, email).filter((r) => r.ok).length;
  if (!password) return { level: 0, label: "" };
  if (password.length < 10 || cumplidas < 3) return { level: 1, label: "Clave débil" };
  if (password.length < 14) return { level: 2, label: "Clave aceptable" };
  return { level: 3, label: "Clave sólida" };
};
